import { Diagram } from './Diagram';

/**
 * Hand-authored SVG diagram of the token-prediction loop.
 * Used in Lesson 1.2 teaching section after Step 5 is introduced.
 *
 * Visual: a horizontal flow of 4 nodes (Input text → Transformer → Probabilities → Sample one token)
 * with a return arrow looping the picked token back to "Input text."
 *
 * Colors are baked in (not tokens) so the SVG is self-contained.
 *   ink:     #0A0A0B   (used as fill behind for contrast at small sizes, replaced by elevated)
 *   elevated:#181820
 *   surface: #111114
 *   line:    #26262E
 *   text:    #E6E6EA
 *   muted:   #8A8A95
 *   amber:   #FFB547
 */
export function TokenLoopDiagram() {
  return (
    <Diagram
      figure="1.2"
      title="The token-prediction loop"
      caption="The model predicts one token at a time, adds it back to the input, and runs the loop again. Every AI response you have ever seen was built this way."
      ariaLabel="A horizontal flow with four boxes: Input text, Transformer, Probability distribution, and Sample one token. An arrow loops from the final box back to the first, labelled 'add token, repeat.'"
    >
      <svg
        viewBox="0 0 760 280"
        xmlns="http://www.w3.org/2000/svg"
        className="block w-full h-auto"
        role="img"
      >
        <title>The token-prediction loop</title>
        <desc>
          The model predicts one token at a time, adds it back to the input,
          and runs the loop again.
        </desc>

        {/* Defs: arrow marker */}
        <defs>
          <marker
            id="arrow-amber"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="#FFB547" />
          </marker>
          <marker
            id="arrow-muted"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="#8A8A95" />
          </marker>
        </defs>

        {/* Node positions: 4 boxes evenly spaced, y=80, w=150 h=80 */}
        {/* Box 1: Input text */}
        <g>
          <rect
            x="20"
            y="80"
            width="150"
            height="80"
            rx="8"
            fill="#181820"
            stroke="#FFB547"
            strokeOpacity="0.5"
          />
          <text
            x="95"
            y="108"
            textAnchor="middle"
            fill="#FFB547"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="1.5"
          >
            01
          </text>
          <text
            x="95"
            y="130"
            textAnchor="middle"
            fill="#E6E6EA"
            fontFamily="Inter, sans-serif"
            fontSize="14"
            fontWeight="600"
          >
            Input text
          </text>
          <text
            x="95"
            y="148"
            textAnchor="middle"
            fill="#8A8A95"
            fontFamily="Inter, sans-serif"
            fontSize="11"
          >
            chopped to tokens
          </text>
        </g>

        {/* Arrow 1 → 2 */}
        <line
          x1="174"
          y1="120"
          x2="216"
          y2="120"
          stroke="#FFB547"
          strokeWidth="2"
          markerEnd="url(#arrow-amber)"
        />

        {/* Box 2: Transformer */}
        <g>
          <rect
            x="220"
            y="80"
            width="150"
            height="80"
            rx="8"
            fill="#181820"
            stroke="#FFB547"
            strokeOpacity="0.5"
          />
          <text
            x="295"
            y="108"
            textAnchor="middle"
            fill="#FFB547"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="1.5"
          >
            02
          </text>
          <text
            x="295"
            y="130"
            textAnchor="middle"
            fill="#E6E6EA"
            fontFamily="Inter, sans-serif"
            fontSize="14"
            fontWeight="600"
          >
            Transformer
          </text>
          <text
            x="295"
            y="148"
            textAnchor="middle"
            fill="#8A8A95"
            fontFamily="Inter, sans-serif"
            fontSize="11"
          >
            weighs every token
          </text>
        </g>

        {/* Arrow 2 → 3 */}
        <line
          x1="374"
          y1="120"
          x2="416"
          y2="120"
          stroke="#FFB547"
          strokeWidth="2"
          markerEnd="url(#arrow-amber)"
        />

        {/* Box 3: Probability distribution */}
        <g>
          <rect
            x="420"
            y="80"
            width="150"
            height="80"
            rx="8"
            fill="#181820"
            stroke="#FFB547"
            strokeOpacity="0.5"
          />
          <text
            x="495"
            y="108"
            textAnchor="middle"
            fill="#FFB547"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="1.5"
          >
            03
          </text>
          <text
            x="495"
            y="130"
            textAnchor="middle"
            fill="#E6E6EA"
            fontFamily="Inter, sans-serif"
            fontSize="14"
            fontWeight="600"
          >
            Probabilities
          </text>
          <text
            x="495"
            y="148"
            textAnchor="middle"
            fill="#8A8A95"
            fontFamily="Inter, sans-serif"
            fontSize="11"
          >
            ranked next tokens
          </text>
        </g>

        {/* Arrow 3 → 4 */}
        <line
          x1="574"
          y1="120"
          x2="616"
          y2="120"
          stroke="#FFB547"
          strokeWidth="2"
          markerEnd="url(#arrow-amber)"
        />

        {/* Box 4: Sample one token */}
        <g>
          <rect
            x="620"
            y="80"
            width="150"
            height="80"
            rx="8"
            fill="#181820"
            stroke="#FFB547"
            strokeOpacity="0.5"
          />
          <text
            x="695"
            y="108"
            textAnchor="middle"
            fill="#FFB547"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            letterSpacing="1.5"
          >
            04
          </text>
          <text
            x="695"
            y="130"
            textAnchor="middle"
            fill="#E6E6EA"
            fontFamily="Inter, sans-serif"
            fontSize="14"
            fontWeight="600"
          >
            Sample
          </text>
          <text
            x="695"
            y="148"
            textAnchor="middle"
            fill="#8A8A95"
            fontFamily="Inter, sans-serif"
            fontSize="11"
          >
            pick one token
          </text>
        </g>

        {/* Loop-back path: from box 4 bottom, down, across, up to box 1 bottom */}
        <path
          d="M 695,160 L 695,220 L 95,220 L 95,160"
          fill="none"
          stroke="#8A8A95"
          strokeWidth="2"
          strokeDasharray="6 4"
          markerEnd="url(#arrow-muted)"
        />

        {/* Loop label */}
        <rect
          x="320"
          y="208"
          width="150"
          height="24"
          rx="4"
          fill="#0A0A0B"
        />
        <text
          x="395"
          y="225"
          textAnchor="middle"
          fill="#8A8A95"
          fontFamily="JetBrains Mono, monospace"
          fontSize="11"
          letterSpacing="0.5"
        >
          add token, repeat
        </text>
      </svg>
    </Diagram>
  );
}
